/* System defined core library */
import { Component, OnInit, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

/* User defined library/components */
import { DemographicsService } from '../../../shared/services/demographics.service';
import { DynamicComponentService } from '../../../shared/services/dynamic.component.service';
import { RoutingService } from '../../../shared/services/routing.service';
import { UtilityService } from '../../../shared/services/utility.service';
import { OrchestrationApiService } from '../../shared/services/orchestration-api.service';
import { SelectedExamDetailService } from '../../shared/services/selected-exam-detail.service';

/* User defined models */
import { ContentResponse } from '../../../shared/models/common/shared.model';
import { Candidate } from '../../../shared/models/scheduling/candidate.model';

/* User defined enums/constants */
import { TargetFlow, ExternalUrls } from '../../../shared/constants/scheduling.constant';
import { ComponentType } from '../../../shared/enums/scheduling.enum';

import { ContentComponent } from '../../../shared/components/content/content.component';

@Component({
  selector: 'app-biometric-consent',
  templateUrl: './biometric-consent.component.html',
  styleUrls: ['./biometric-consent.component.scss'],
  providers: [DynamicComponentService]
})
export class BiometricConsentComponent implements OnInit {

  /* User defined private/public variables */
  public candidate: Candidate;
  private client: any;
  public consentOptions: string[] = ['I Consent', 'I Do Not Consent'];
  public content: ContentResponse;
  public dataAgreeText: string;
  public isBiometricConsentChecked: boolean;
  public isBiometricConsentCheckedReschedule: boolean = false;
  public isBiometricConsentCheckedSchedule: boolean = false;
  public isDataConsentChecked: boolean;
  private program: any;
  public resourceFileCulture: string = '';
  private selectedAppointments: any;
  public targetFlow: string = '';
  public anchorStart: string = `<a target="_blank" href="${ExternalUrls.PrometricPrivacyPolicyUrl}">`;
  public anchorEnd: string = '</a>';

  @ViewChild(ContentComponent) contentInstance: ContentComponent;

  /* Service subscriptions */
  candidate$: Subscription;
  client$: Subscription;
  program$: Subscription;
  resourceFileCulture$: Subscription;
  selectedAppointments$: Subscription;
  translateLanguage$: Subscription;

  /* Constructor declaration with required injected services */
  constructor(
    private demographicsService: DemographicsService,
    private dynamicComponentService: DynamicComponentService,
    private orchestrationApiService: OrchestrationApiService,
    private routingService: RoutingService,
    private selectedExamDetailService: SelectedExamDetailService,
    private translate: TranslateService,
    private utilityService: UtilityService
  ) {
    this.candidate$ = selectedExamDetailService.candidate.subscribe(
      candidate => {
        this.candidate = candidate;
      });

    this.client$ = this.selectedExamDetailService.client.subscribe(
      data => {
        this.client = data;
      });

    this.program$ = this.selectedExamDetailService.program.subscribe(
      data => {
        this.program = data;
      });

    // update biometric content on changing language from dropdown
    this.resourceFileCulture$ = this.selectedExamDetailService.resourceFileCulture.subscribe(
      resourceFileCulture => {
        this.resourceFileCulture = resourceFileCulture;
        this.getContent();
      });

    this.selectedAppointments$ = this.selectedExamDetailService.selectedAppointmentDetails.subscribe(
      data => {
        this.selectedAppointments = data;
      });

    translate.setDefaultLang(this.selectedExamDetailService.defaultLanguageCode);
    this.translateLanguage$ = selectedExamDetailService.languageCode.subscribe(
      languageCode => {
        translate.use(languageCode);
      });

    this.targetFlow = this.selectedExamDetailService.currentTargetFlow;
  }

  /* Initialize the component & call required methods when component loaded first time. */
  ngOnInit() {
    sessionStorage.setItem('previousUrl', window.location.pathname);
    if (!this.utilityService.isDataValidToLoadComponent(ComponentType.BiometricConsentComponent)) { return; }
    this.content = new ContentResponse();
    // temp fix- when component loads resoucefileculture is null from service so reading from examDetails.
    this.getContent();
  }

  ngAfterViewInit() {
    this.contentInstance.setFocusToContent();
  }

  /* Move to next page */
  submit(): void {
    if (this.targetFlow === TargetFlow.Schedule) {
      this.orchestrationApiService.getFlows(this.client.code, this.program.code, this.selectedAppointments[0].exam.code).subscribe((response: any) => {
        if (response.length > 0) {
          this.selectedExamDetailService.isBiometricConsentCheckedSchedule = this.isBiometricConsentCheckedSchedule;
          this.routingService.scheduleFwdNavigation(ComponentType.BiometricConsentComponent);
        }
      });
    } else {
      this.selectedExamDetailService.isBiometricConsentCheckedReschedule = this.isBiometricConsentCheckedReschedule;
      this.routingService.rescheduleFwdNavigation(ComponentType.BiometricConsentComponent);
    }
  }

  /* Move back a page. */
  goToPrevPage(): void {
    this.routingService.backwardNavigation(ComponentType.BiometricConsentComponent);
  }

  /* Call the biometric content from Xml resource file */
  getContent(): void {
    this.utilityService.getContent('BiometricConsent', this.resourceFileCulture, null).subscribe(content => {
      this.content = content;
      this.translateDataAgreement();
    });
  }

  /* Piece together the translations of the data transfer agreement text */
  translateDataAgreement(): void {
    this.translate.get('contentComponent.here', { url: ExternalUrls.PrometricPrivacyPolicyUrl }).subscribe(translatedHere => {
      let translatedHyperlink: string = this.anchorStart + translatedHere + this.anchorEnd;
      this.translate.get('contentComponent.biometricDataTransferCheckboxText', { hyperlink: translatedHyperlink }).subscribe(value => {
        this.dataAgreeText = value;
      });
    });

    this.translate.onLangChange.subscribe(() => {
      this.translate.get('contentComponent.here', { url: ExternalUrls.PrometricPrivacyPolicyUrl }).subscribe(translatedHere => {
        let translatedHyperlink: string = this.anchorStart + translatedHere + this.anchorEnd;
        this.translate.get('contentComponent.biometricDataTransferCheckboxText', { hyperlink: translatedHyperlink }).subscribe(value => {
          this.dataAgreeText = value;
        });
      });
    });
  }

  /* On biometric consent change */
  setConsentSelected(event: any): void {
    this.isBiometricConsentChecked = event;
    this.setInFlow();
  }

  /* On data consent change */
  setDataSelected(event: any): void {
    this.isDataConsentChecked = event;
    this.setInFlow();
  }

  /* Set consent in flow */
  setInFlow(): void {
    let isAllAccepted = this.isBiometricConsentChecked && this.isDataConsentChecked;

    if (this.targetFlow === TargetFlow.Schedule) {
      this.isBiometricConsentCheckedSchedule = isAllAccepted;
      this.selectedExamDetailService.isBiometricConsentCheckedSchedule = isAllAccepted;
    } else {
      this.isBiometricConsentCheckedReschedule = isAllAccepted;
      this.selectedExamDetailService.isBiometricConsentCheckedReschedule = isAllAccepted;
    }
  }

  isDisable(): boolean {
    return (!this.isBiometricConsentChecked || !this.isDataConsentChecked);
  }
}
